/**
 * A.L.D.O 2.0 - Permission Hook
 * Hook para verificar permisos del usuario actual
 *
 * Reference: ALDO_RBAC_MODEL.md
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission()
 *
 *   // Check single permission
 *   if (hasPermission('pm.discovery.read')) { ... }
 *
 *   // Check any of multiple permissions
 *   if (hasAnyPermission(['pm.discovery.read', 'pm.analysis.read'])) { ... }
 *
 *   // Check all permissions required
 *   if (hasAllPermissions(['pm.discovery.read', 'pm.discovery.execute'])) { ... }
 */
import { useMemo, useCallback } from 'react'
import { useAuthStore } from '@/store/auth'

export interface PermissionHookResult {
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean
  /** Check if user has ANY of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean
  /** Check if user has ALL of the specified permissions */
  hasAllPermissions: (permissions: string[]) => boolean
  /** Check if user has a specific role */
  hasRole: (roleCode: string) => boolean
  /** Check if a feature flag is enabled */
  isFeatureEnabled: (featureCode: string) => boolean
  /** INC-077: Check if user has access to a product */
  hasProduct: (productCode: string) => boolean
  /** Get user's role level (1=GAM, 2=CP, 3=Holding, etc.) */
  roleLevel: number
  /** Check if user is at least admin level (role_level <= 3) */
  isAdmin: boolean
  /** Check if user is GAM admin (role_level === 1) */
  isGAMAdmin: boolean
  /** User's effective permissions list */
  permissions: string[]
  /** User's assigned roles */
  roles: Array<{ code: string; name: string; scope_type: string }>
  /** Enabled feature flags */
  features: string[]
  /** INC-077: Products available for current holding */
  products: string[]
  /** Loading state */
  isLoading: boolean
}

/**
 * Hook to check user permissions, roles, and feature flags
 */
export function usePermission(): PermissionHookResult {
  const {
    user,
    permissions: storePermissions,
    roles: storeRoles,
    features: storeFeatures,
    products: storeProducts,
    isLoading,
  } = useAuthStore()

  // Memoize permissions set for O(1) lookup
  const permissionsSet = useMemo(() => {
    return new Set(storePermissions || [])
  }, [storePermissions])

  // Memoize roles set for O(1) lookup
  const rolesSet = useMemo(() => {
    return new Set((storeRoles || []).map((r) => r.code))
  }, [storeRoles])

  // Memoize features set for O(1) lookup
  const featuresSet = useMemo(() => {
    return new Set(storeFeatures || [])
  }, [storeFeatures])

  // INC-077: Memoize products set for O(1) lookup
  const productsSet = useMemo(() => {
    return new Set(storeProducts || [])
  }, [storeProducts])

  // Get role level from user
  const roleLevel = user?.role_level ?? 7 // Default to basic user

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission: string): boolean => {
      // GAM Admin (role_level 1) has all permissions
      if (roleLevel === 1) return true
      return permissionsSet.has(permission)
    },
    [permissionsSet, roleLevel]
  )

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (roleLevel === 1) return true
      return permissions.some((p) => permissionsSet.has(p))
    },
    [permissionsSet, roleLevel]
  )

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      if (roleLevel === 1) return true
      return permissions.every((p) => permissionsSet.has(p))
    },
    [permissionsSet, roleLevel]
  )

  // Check if user has a specific role
  const hasRole = useCallback(
    (roleCode: string): boolean => {
      if (roleLevel === 1) return true // GAM has all roles implicitly
      return rolesSet.has(roleCode)
    },
    [rolesSet, roleLevel]
  )

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback(
    (featureCode: string): boolean => {
      return featuresSet.has(featureCode)
    },
    [featuresSet]
  )

  // INC-077: Check if user has access to a product
  const hasProduct = useCallback(
    (productCode: string): boolean => {
      // GAM Admin (role_level 1) has all products
      if (roleLevel === 1) return true
      return productsSet.has(productCode)
    },
    [productsSet, roleLevel]
  )

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isFeatureEnabled,
    hasProduct,
    roleLevel,
    isAdmin: roleLevel <= 3,
    isGAMAdmin: roleLevel === 1,
    permissions: storePermissions || [],
    roles: storeRoles || [],
    features: storeFeatures || [],
    products: storeProducts || [],
    isLoading,
  }
}

/**
 * Hook to check a single permission (simpler API)
 *
 * Usage:
 *   const canReadDiscovery = useHasPermission('pm.discovery.read')
 */
export function useHasPermission(permission: string): boolean {
  const { hasPermission } = usePermission()
  return hasPermission(permission)
}

/**
 * Hook to check if user has a minimum role level
 *
 * Usage:
 *   const isHoldingAdmin = useMinRoleLevel(3) // true if role_level <= 3
 */
export function useMinRoleLevel(maxLevel: number): boolean {
  const { roleLevel } = usePermission()
  return roleLevel <= maxLevel
}

/**
 * Hook to check if a feature is enabled
 *
 * Usage:
 *   const pmEnabled = useFeatureFlag('module_process_mining')
 */
export function useFeatureFlag(featureCode: string): boolean {
  const { isFeatureEnabled } = usePermission()
  return isFeatureEnabled(featureCode)
}

export default usePermission
